import { ScrollableRegion } from '@syncanix/ui';

import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { subprocessors } from '../../content/subprocessors';

import type { ReactElement } from 'react';

/**
 * Phase 6 Pass 3 iter 243 — accessible subprocessor table.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/subprocessors` +
 * § Audit gates → Architecture (single source of truth via JSON).
 *
 * **Accessibility (Designer + Accessibility hat, locked)**:
 *
 *   - Semantic `<table>` with `<caption>` so screen readers announce
 *     it as a data table.
 *   - `scope="col"` on every header per WAI-ARIA tables practice.
 *   - Column order locked at "Subprocessor / Purpose / Primary
 *     location / Data categories / Transfer mechanism" — matches
 *     the procurement reader's question order ("who, what, where,
 *     what flows through, under what mechanism").
 *   - Vendor name renders as an external link (rel=noopener +
 *     target=_blank) so a reader can click through to verify.
 *
 * **Mobile responsiveness (Designer hat)**: at narrow widths the
 * table is horizontally scrollable inside its parent. Cards-on-
 * mobile is a polish item for v1.1 — the table layout already
 * works on mobile via horizontal scroll which procurement readers
 * are familiar with from DPA addenda PDFs.
 *
 * **Three-layer contract preserved**: pure presentational, no
 * state, no effects. Data source is the compile-time JSON import.
 */
export function SubprocessorTable(): ReactElement {
  const { t } = useT();
  return (
    <ScrollableRegion aria-label={t('subprocessors.table.caption')} className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="caption-top mb-3 text-start text-sm text-[color:var(--bd-muted)]">
          {t('subprocessors.table.caption')}
        </caption>
        <thead>
          <tr className="border-b border-[color:var(--bd-line)] text-start">
            <th scope="col" className="py-2 pe-4 text-start font-semibold">
              {t('subprocessors.table.colName')}
            </th>
            <th scope="col" className="py-2 pe-4 text-start font-semibold">
              {t('subprocessors.table.colPurpose')}
            </th>
            <th scope="col" className="py-2 pe-4 text-start font-semibold">
              {t('subprocessors.table.colLocation')}
            </th>
            <th scope="col" className="py-2 pe-4 text-start font-semibold">
              {t('subprocessors.table.colCategories')}
            </th>
            <th scope="col" className="py-2 pe-4 text-start font-semibold">
              {t('subprocessors.table.colTransfer')}
            </th>
          </tr>
        </thead>
        <tbody>
          {subprocessors.map((p) => (
            <tr key={p.id} className="border-b border-[color:var(--bd-line)] align-top">
              <td className="py-3 pe-4 font-medium">
                <a
                  className={INLINE_LINK}
                  href={p.website}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {p.name}
                </a>
              </td>
              <td className="py-3 pe-4">{t(p.purposeKey)}</td>
              <td className="py-3 pe-4">{t(p.locationKey)}</td>
              <td className="py-3 pe-4">
                <ul className="list-disc ps-4">
                  {p.dataKeys.map((dataKey) => (
                    <li key={dataKey}>{t(dataKey)}</li>
                  ))}
                </ul>
              </td>
              <td className="py-3 pe-4">{t(p.transferKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollableRegion>
  );
}
